/**
 * Read-only view of a tenant's `configurations` rows.
 *
 * Every judgement shown here is computed and tested in the backend
 * (app/services/tenant_config_reader.py); this component only chooses which of
 * them to show and how, deliberately — the frontend has no tests yet.
 */
import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import { projectAPI, TenantConfigResponse, TenantValueCheck } from '../services/api';
import LeadershipBriefReadings from './executive/LeadershipBriefReadings';

interface Props {
  projectId: string;
}

/** A wrong `auth` row is a symptom; the container app's env var is the cause. */
const ENV_VAR_NOTE =
  'GUS rewrites this row from the container app’s PROJECT_SLUG environment ' +
  'variable every time the app restarts, so editing the database alone will not ' +
  'hold. Change the env var on the container app.';

/** What each checked key is for, in English and Arabic. */
const CHECK_LABELS: Record<string, { en: string; ar: string }> = {
  CITY_NAME: { en: 'City name', ar: 'اسم المدينة (إنجليزي)' },
  CITY_NAME_AR: { en: 'City name in Arabic', ar: 'اسم المدينة بالعربية' },
  CITY_PEOPLE_NOUN: { en: 'Word for the people who live here', ar: 'الكلمة الدالة على سكان المكان (إنجليزي)' },
  CITY_PEOPLE_NOUN_AR: { en: 'Word for the people, in Arabic', ar: 'الكلمة الدالة على سكان المكان' },
  CITY_PLACE_NOUN: { en: 'Word for the place', ar: 'الكلمة الدالة على المكان (إنجليزي)' },
  CITY_PLACE_NOUN_AR: { en: 'Word for the place, in Arabic', ar: 'الكلمة الدالة على المكان' },
  BRIEF_AI_TIMEOUT_S: { en: 'Brief: seconds to wait for the AI', ar: 'الموجز: ثوانٍ لانتظار الذكاء الاصطناعي' },
  BRIEF_MAX_COMPLETION_TOKENS: { en: 'Brief: AI token limit', ar: 'الموجز: حد رموز الذكاء الاصطناعي' },
  KPI_READINGS_SCHEDULE_ENABLED: { en: 'Daily readings check', ar: 'الفحص اليومي للقراءات' },
  KPI_READINGS_UTC_HOUR: { en: 'Hour of the daily check (UTC)', ar: 'ساعة الفحص اليومي (بالتوقيت العالمي)' },
  EXECUTIVE_DECK_KEEP_HOURS: { en: 'Report: hours a finished report is kept', ar: 'التقرير: ساعات الاحتفاظ بالتقرير الجاهز' },
  EXECUTIVE_DECK_CONCURRENCY: { en: 'Report: reports built at once', ar: 'التقرير: عدد التقارير التي تُبنى معًا' },
  EXECUTIVE_DECK_LLM_TIMEOUT_S: { en: 'Report: seconds to wait for the AI', ar: 'التقرير: ثوانٍ لانتظار الذكاء الاصطناعي' },
};

/** The Leadership Brief's operations keys, shown in their own section. */
const EXECUTIVE_KEYS = [
  'KPI_READINGS_SCHEDULE_ENABLED',
  'KPI_READINGS_UTC_HOUR',
  'EXECUTIVE_DECK_KEEP_HOURS',
  'EXECUTIVE_DECK_CONCURRENCY',
  'EXECUTIVE_DECK_LLM_TIMEOUT_S',
];

const CHECK_CHIP: Record<TenantValueCheck['state'], { label: string; color: 'success' | 'default' | 'error' | 'warning' }> = {
  ok: { label: 'used / مستخدم', color: 'success' },
  unset: { label: 'default / افتراضي', color: 'default' },
  invalid: { label: 'ignored by GUS / يتجاهله GUS', color: 'error' },
  not_read: { label: 'not read by GUS yet / لا يقرؤه GUS بعد', color: 'warning' },
};

/** A check's message in both languages. */
const CheckMessage: React.FC<{ check: TenantValueCheck }> = ({ check }) => (
  <>
    {check.message}
    {check.message_ar && (
      <Box component="span" lang="ar" dir="rtl" sx={{ display: 'block' }}>
        {check.message_ar}
      </Box>
    )}
  </>
);

/** One row per checked key: what it is for, its value, whether GUS uses it, and what that means. */
const ChecksTable: React.FC<{ checks: TenantValueCheck[] }> = ({ checks }) => (
  <TableContainer component={Paper} variant="outlined">
    <Table size="small">
      <TableHead>
        <TableRow>
          <TableCell>Setting / الإعداد</TableCell>
          <TableCell>Value / القيمة</TableCell>
          <TableCell>GUS</TableCell>
          <TableCell>What it means / ما يعنيه</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {checks.map((c) => (
          <TableRow key={c.key}>
            <TableCell>
              {CHECK_LABELS[c.key]?.en ?? c.key}
              <Box component="span" lang="ar" dir="rtl" sx={{ display: 'block', color: 'text.secondary', fontSize: 12 }}>
                {CHECK_LABELS[c.key]?.ar}
              </Box>
              <Box component="span" sx={{ fontFamily: 'monospace', fontSize: 11, color: 'text.secondary' }}>
                {c.key}
              </Box>
            </TableCell>
            <TableCell sx={{ fontFamily: 'monospace', fontSize: 12 }}>{c.value ?? '—'}</TableCell>
            <TableCell>
              <Chip size="small" label={CHECK_CHIP[c.state].label} color={CHECK_CHIP[c.state].color} />
            </TableCell>
            <TableCell>
              <Typography variant="body2" color="text.secondary"><CheckMessage check={c} /></Typography>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  </TableContainer>
);

const TenantConfigPanel: React.FC<Props> = ({ projectId }) => {
  const [data, setData] = useState<TenantConfigResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      setData(await projectAPI.getTenantConfig(projectId));
    } catch (e: any) {
      setLoadError(
        e?.response?.data?.detail || e?.message || 'Failed to load tenant configuration'
      );
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    load();
  }, [load]);

  const retry = (
    <Button size="small" startIcon={<RefreshIcon />} onClick={load} disabled={loading}>
      Retry
    </Button>
  );

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }
  if (loadError) {
    return (
      <Alert severity="error" action={retry}>
        {loadError}
      </Alert>
    );
  }
  if (!data) {
    // Reachable if the API returns an empty body. Rendering nothing would leave
    // a blank tab with no way to tell what happened.
    return (
      <Alert severity="error" action={retry}>
        No response from the portal API.
      </Alert>
    );
  }

  const shared = data.shared_with.length > 0;
  const checks = data.checks || [];
  const wordChecks = checks.filter((c) => !EXECUTIVE_KEYS.includes(c.key));
  const executiveChecks = checks.filter((c) => EXECUTIVE_KEYS.includes(c.key));
  const arabicName = checks.find((c) => c.key === 'CITY_NAME_AR');
  const ignored = checks.filter((c) => c.state === 'invalid');
  // Listed in the order GUS resolves them, and GUS uses the last.
  const slugCategory = data.slug_categories[data.slug_categories.length - 1];
  // GUS rewrites only the `auth` row when it starts, so the env var fixes that
  // row and no other.
  const slugFix =
    slugCategory === 'auth'
      ? ENV_VAR_NOTE
      : `This value is in the “${slugCategory}” row, which GUS does not rewrite when ` +
        'it restarts — correct or remove that row.';

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="body2" color="text.secondary">
          Read from the database recorded for this project in the portal. Read-only.
        </Typography>
        {retry}
      </Box>

      {arabicName && arabicName.state !== 'ok' && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          <AlertTitle>
            This tenant has no Arabic city name{' '}
            <Box component="span" lang="ar" dir="rtl">(لا يوجد اسم عربي للمدينة)</Box>
          </AlertTitle>
          <CheckMessage check={arabicName} /> Set <code>CITY_NAME_AR</code> in the tenant&apos;s configurations table.
          The portal cannot write that table.
          <Box component="span" lang="ar" dir="rtl" sx={{ display: 'block' }}>
            اضبط <code>CITY_NAME_AR</code> في جدول الإعدادات لدى المستأجر. لا تستطيع البوابة الكتابة في ذلك الجدول.
          </Box>
        </Alert>
      )}

      {ignored.length > 0 && (
        <Alert severity="error" sx={{ mb: 2 }}>
          <AlertTitle>
            GUS ignores {ignored.length === 1 ? 'this value' : 'these values'}{' '}
            <Box component="span" lang="ar" dir="rtl">
              (يتجاهل GUS {ignored.length === 1 ? 'هذه القيمة' : 'هذه القيم'})
            </Box>
          </AlertTitle>
          {ignored.map((c) => (
            <Box key={c.key} sx={{ mb: 0.5 }}>
              <code>{c.key}</code> = &quot;{c.value}&quot;: <CheckMessage check={c} />
            </Box>
          ))}
        </Alert>
      )}

      {data.slug_state === 'mismatch' && (
        <Alert severity="error" sx={{ mb: 2 }}>
          <AlertTitle>This tenant calls itself &quot;{data.slug_value}&quot;</AlertTitle>
          The project is <strong>{data.project_slug}</strong>. GUS sends the tenant&apos;s
          value to the portal when authorising a login, and the portal compares it exactly,
          so people will be refused unless they hold access to &quot;{data.slug_value}&quot;.
          {shared && (
            <>
              {' '}
              Because this database is shared with <strong>{data.shared_with.join(', ')}</strong>,
              there is only one such row for all of them — whichever app restarted most
              recently owns it, so this cannot be correct for both projects at once.
            </>
          )}{' '}
          {slugFix}
        </Alert>
      )}

      {data.slug_state === 'empty' && (
        <Alert severity="error" sx={{ mb: 2 }}>
          <AlertTitle>PROJECT_SLUG is empty, so anyone who can sign in gets in</AlertTitle>
          GUS skips its project-access check when this value is empty and lets every
          signed-in user into this tenant.{' '}
          {slugCategory === 'auth'
            ? 'Set PROJECT_SLUG on the container app; GUS writes it into this row when the app starts.'
            : slugFix}
        </Alert>
      )}

      {data.slug_categories.length > 1 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          PROJECT_SLUG is set in more than one category (
          <strong>{data.slug_categories.join(', ')}</strong>). GUS uses the last of these,
          &quot;{slugCategory}&quot;, and ignores the rest.
        </Alert>
      )}

      {data.slug_state === 'subdomain' && (
        <Alert severity="info" sx={{ mb: 2 }}>
          This tenant identifies itself by its subdomain (&quot;{data.slug_value}&quot;) rather
          than its project slug (&quot;{data.project_slug}&quot;). Logins still work — the portal
          accepts either — but the two spellings are worth reconciling.
        </Alert>
      )}

      {data.slug_state === 'absent' && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          No PROJECT_SLUG row is set in this tenant. GUS will fall back to its environment
          variable, which this screen cannot see.
        </Alert>
      )}

      {data.slug_state === 'unknown' && data.reachable === false && (
        <Alert severity="info" sx={{ mb: 2 }}>
          PROJECT_SLUG could not be checked, because the tenant was not readable. This is
          not a clean bill of health.
        </Alert>
      )}

      {data.recorded_database && data.database && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          <AlertTitle>These settings may be from the wrong database</AlertTitle>
          They were read through the portal&apos;s read-only login, which points at{' '}
          <Box component="span" sx={{ fontFamily: 'monospace' }}>
            {data.database.host}/{data.database.name}
          </Box>
          , but this project&apos;s database is recorded as{' '}
          <Box component="span" sx={{ fontFamily: 'monospace' }}>
            {data.recorded_database.host}/{data.recorded_database.name}
          </Box>
          . The read-only login is only replaced once it stops working, so it can outlive a
          database move.
        </Alert>
      )}

      {shared && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          This database is shared with <strong>{data.shared_with.join(', ')}</strong>. These
          are the same rows those projects read, and a change made for one applies to all of
          them.
        </Alert>
      )}

      {data.sharing_unknown.length > 0 && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Could not compare against <strong>{data.sharing_unknown.join(', ')}</strong> — their
          stored connection details could not be read, so sharing with those projects is
          unknown.
        </Alert>
      )}

      {!data.readable && (
        <Alert severity={data.reachable ? 'error' : 'warning'} sx={{ mb: 2 }} action={retry}>
          <AlertTitle>
            {data.reachable
              ? 'The tenant answered, but its settings could not be read'
              : 'Could not reach this tenant'}
          </AlertTitle>
          {data.database && (
            <Box component="span" sx={{ fontFamily: 'monospace' }}>
              {data.database.host}/{data.database.name}
            </Box>
          )}
          {data.database ? ' — ' : ''}
          {data.error}
        </Alert>
      )}

      {data.database && (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: 'block', mb: 2, fontFamily: 'monospace' }}
        >
          {data.database.host} &middot; {data.database.name}
          {data.credential ? ` · ${data.credential} credential` : ''}
        </Typography>
      )}

      {wordChecks.length > 0 && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" gutterBottom>
            City names and words, and Brief limits{' '}
            <Box component="span" lang="ar" dir="rtl" sx={{ color: 'text.secondary', fontWeight: 400 }}>
              أسماء المدينة وكلماتها، وحدود الموجز
            </Box>
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            GUS reads these from the tenant&apos;s configurations table (when its container has
            DATABASE_CONFIG=true) and uses its own default when one is missing or unusable. A row, even an empty one,
            comes before the container app&apos;s environment variable. Read-only: change them in the tenant&apos;s
            database or the container app&apos;s environment.
            <Box component="span" lang="ar" dir="rtl" sx={{ display: 'block' }}>
              يقرأ GUS هذه القيم من جدول الإعدادات لدى المستأجر (عندما تكون DATABASE_CONFIG=true في حاويته)، ويستخدم
              قيمته الافتراضية حين تكون إحداها مفقودة أو غير صالحة. والصف، ولو كان فارغًا، يتقدّم على متغير البيئة في
              تطبيق الحاوية. للقراءة فقط: غيّرها في قاعدة بيانات المستأجر أو في بيئة تطبيق الحاوية.
            </Box>
          </Typography>
          <ChecksTable checks={wordChecks} />
        </Box>
      )}

      <Box sx={{ mb: 2 }}>
        <Typography variant="subtitle2" gutterBottom>
          Leadership Brief: readings and report{' '}
          <Box component="span" lang="ar" dir="rtl" sx={{ color: 'text.secondary', fontWeight: 400 }}>
            الموجز القيادي: القراءات والتقرير
          </Box>
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          GUS saves one reading per KPI per month for the Leadership Brief tab. The button and the status below go to
          this project&apos;s GUS as you, so your account must be a GUS admin there. The tab itself and its settings
          are on the Features tab.
          <Box component="span" lang="ar" dir="rtl" sx={{ display: 'block' }}>
            يحفظ GUS قراءة واحدة لكل مؤشر في كل شهر لتبويب الموجز القيادي. يتصل الزر والحالة أدناه بـ GUS الخاص بهذا
            المشروع باسمك، لذا يجب أن يكون حسابك مشرفًا فيه. التبويب نفسه وإعداداته في تبويب الميزات.
          </Box>
        </Typography>
        <LeadershipBriefReadings projectId={projectId} />
        {executiveChecks.length > 0 ? (
          <ChecksTable checks={executiveChecks} />
        ) : (
          <Typography variant="body2" color="text.secondary">
            The schedule and report settings could not be read from this tenant (see above).
            <Box component="span" lang="ar" dir="rtl" sx={{ display: 'block' }}>
              تعذّرت قراءة إعدادات الجدولة والتقرير من هذا المستأجر (انظر أعلاه).
            </Box>
          </Typography>
        )}
      </Box>

      {data.readable && (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Key</TableCell>
                <TableCell>Value</TableCell>
                <TableCell>Category</TableCell>
                <TableCell>Last changed</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.config.length === 0 && (
                <TableRow>
                  <TableCell colSpan={4}>
                    <Typography variant="body2" color="text.secondary">
                      None of the settings this screen watches are set in this tenant.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
              {data.config.map((row, i) => (
                // key includes the index because (key, category) is the real
                // primary key, so the same key can legitimately appear twice.
                <TableRow key={`${row.key}-${row.category ?? ''}-${i}`}>
                  <TableCell sx={{ fontFamily: 'monospace', fontSize: 12 }}>
                    {row.key}
                  </TableCell>
                  <TableCell sx={{ fontFamily: 'monospace', fontSize: 12 }}>
                    {row.value ?? <em>(empty)</em>}
                    {row.key === 'PROJECT_SLUG' &&
                      row.category === slugCategory &&
                      data.slug_state === 'mismatch' && (
                        <Chip label="mismatch" color="error" size="small" sx={{ ml: 1 }} />
                      )}
                  </TableCell>
                  <TableCell>{row.category ?? '—'}</TableCell>
                  <TableCell>
                    {row.updated_at ? new Date(row.updated_at).toLocaleString() : '—'}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

export default TenantConfigPanel;
